import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { 
  ArrowLeft, Save, Eye, Send, Sun, Moon, Check, Clock, 
  ChevronDown, Copy, Info, Code, Type, Plus,
  Bold, Italic, Underline, List, ListOrdered, Link, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Undo, Redo
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import UnderlineExtension from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryOptions = [
  { value: 'proposals', label: 'Proposals' },
  { value: 'events', label: 'Events' },
  { value: 'crm_renewals', label: 'CRM & Renewals' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'support', label: 'Support' },
  { value: 'internal', label: 'Internal' }
];

// Toolbar Button Component
const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded transition-colors ${
      isActive 
        ? 'bg-[#A2FF00] text-black' 
        : 'text-gray-400 hover:text-white hover:bg-white/10'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

// Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || 'https://');
    
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-white/10 bg-black/30">
      {/* Undo/Redo */}
      <ToolbarButton 
        onClick={() => editor.chain().focus().undo().run()} 
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().redo().run()} 
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-6 bg-white/10 mx-1" />
      
      {/* Headings */}
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} 
        isActive={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} 
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-6 bg-white/10 mx-1" />
      
      {/* Text Formatting */}
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleBold().run()} 
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleItalic().run()} 
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleUnderline().run()} 
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <Underline className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-6 bg-white/10 mx-1" />
      
      {/* Alignment */}
      <ToolbarButton 
        onClick={() => editor.chain().focus().setTextAlign('left').run()} 
        isActive={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().setTextAlign('center').run()} 
        isActive={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().setTextAlign('right').run()} 
        isActive={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-6 bg-white/10 mx-1" />
      
      {/* Lists */}
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleBulletList().run()} 
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton 
        onClick={() => editor.chain().focus().toggleOrderedList().run()} 
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>
      
      <div className="w-px h-6 bg-white/10 mx-1" />
      
      {/* Link */}
      <ToolbarButton 
        onClick={setLink} 
        isActive={editor.isActive('link')}
        title="Insert Link"
      >
        <Link className="w-4 h-4" />
      </ToolbarButton>
    </div>
  );
};

const SystemEmailEditor = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isNew = templateId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState({
    template_key: '',
    name: '',
    category: 'proposals',
    subject: '',
    body: '',
    status: 'draft',
    is_user_editable: true,
    override_sender_email: '',
    override_sender_name: ''
  });
  const [variables, setVariables] = useState([]);
  const [showVariables, setShowVariables] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewTheme, setPreviewTheme] = useState('dark');
  const [editorMode, setEditorMode] = useState('visual');
  const [editorTheme, setEditorTheme] = useState(isDark ? 'dark' : 'light');

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExtension,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-400 underline',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: template.body,
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none min-h-[350px] p-4 focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setTemplate(prev => ({ ...prev, body: html }));
    },
  });

  // Update editor content when template body changes externally
  useEffect(() => {
    if (editor && template.body && editor.getHTML() !== template.body) {
      editor.commands.setContent(template.body);
    }
  }, [editor, template.body]);

  useEffect(() => {
    if (!isNew) {
      fetchTemplate();
    }
  }, [templateId, isNew]);

  useEffect(() => {
    if (template.template_key) {
      fetchVariables(template.template_key);
    }
  }, [template.template_key]);

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/templates/${templateId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplate(data);
      } else {
        alert('Template not found');
        navigate('/admin/system-emails');
      }
    } catch (error) {
      console.error('Failed to fetch template:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVariables = async (key) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/variables/${key}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVariables(data.variables || []);
      }
    } catch (error) {
      console.error('Failed to fetch variables:', error);
    }
  };

  const handleSave = async (newStatus = null) => {
    if (!template.name || !template.subject || !template.body) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const url = isNew 
        ? `${BACKEND_URL}/api/system-emails/templates`
        : `${BACKEND_URL}/api/system-emails/templates/${templateId}`;
      
      const method = isNew ? 'POST' : 'PATCH';
      const payload = isNew ? template : {
        name: template.name,
        subject: template.subject,
        body: template.body,
        status: newStatus || template.status,
        is_user_editable: template.is_user_editable,
        override_sender_email: template.override_sender_email || null,
        override_sender_name: template.override_sender_name || null
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (isNew) {
          navigate(`/admin/system-emails/${data.template_id}/edit`);
        } else {
          setTemplate(data);
          alert('Template saved successfully');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (varName) => {
    const variable = `{{${varName}}}`;
    
    if (editorMode === 'visual' && editor) {
      editor.chain().focus().insertContent(variable).run();
    } else {
      setTemplate(prev => ({
        ...prev,
        body: prev.body + variable
      }));
    }
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
  };

  if (loading) {
    return (
      <AppLayout title="Loading...">
        <div className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>Loading template...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={isNew ? 'New Email Template' : `Edit: ${template.name}`}>
      <style>{`
        .ProseMirror {
          min-height: 350px;
          padding: 16px;
          color: ${editorTheme === 'dark' ? '#e5e5e5' : '#1f2937'};
          background: ${editorTheme === 'dark' ? '#0a0a0a' : '#fff'};
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p {
          margin: 0.75em 0;
        }
        .ProseMirror h1 {
          font-size: 1.75em;
          font-weight: bold;
          margin: 1em 0 0.5em;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 1em 0 0.5em;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.75em 0;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
        .ProseMirror a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #6b7280;
          pointer-events: none;
          height: 0;
          float: left;
        }
      `}</style>
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/system-emails')}
            className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Templates
          </button>
          
          <div className="flex items-center gap-3">
            <div className={`flex items-center p-1 rounded ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
              <button
                onClick={() => setEditorTheme('light')}
                className={`p-2 rounded ${editorTheme === 'light' ? (isDark ? 'bg-white/20' : 'bg-white shadow') : ''}`}
                title="Light editor"
              >
                <Sun className={`w-4 h-4 ${editorTheme === 'light' ? 'text-yellow-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </button>
              <button
                onClick={() => setEditorTheme('dark')}
                className={`p-2 rounded ${editorTheme === 'dark' ? 'bg-white/20' : ''}`}
                title="Dark editor"
              >
                <Moon className={`w-4 h-4 ${editorTheme === 'dark' ? 'text-blue-400' : 'text-gray-500'}`} />
              </button>
            </div>
            
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} rounded-sm transition-colors`}
            >
              <Eye className="w-4 h-4" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            
            {!isNew && template.status === 'draft' && (
              <button
                onClick={() => handleSave('published')}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-green-100 text-green-700 hover:bg-green-200'} rounded-sm transition-colors disabled:opacity-50`}
              >
                <Send className="w-4 h-4" />
                Publish
              </button>
            )}
            
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors disabled:opacity-50`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-3 space-y-4">
            {previewMode ? (
              /* Preview Mode */
              <div className={`${previewTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-white'} rounded-sm border ${previewTheme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                <div className={`flex items-center justify-between px-4 py-3 border-b ${previewTheme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <span className={`text-sm ${previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Email Preview</span>
                  <div className={`flex items-center p-1 rounded ${previewTheme === 'dark' ? 'bg-white/10' : 'bg-gray-100'}`}>
                    <button
                      onClick={() => setPreviewTheme('light')}
                      className={`p-1.5 rounded ${previewTheme === 'light' ? 'bg-white shadow' : ''}`}
                    >
                      <Sun className={`w-3 h-3 ${previewTheme === 'light' ? 'text-yellow-500' : 'text-gray-400'}`} />
                    </button>
                    <button
                      onClick={() => setPreviewTheme('dark')}
                      className={`p-1.5 rounded ${previewTheme === 'dark' ? 'bg-white/20' : ''}`}
                    >
                      <Moon className={`w-3 h-3 ${previewTheme === 'dark' ? 'text-blue-400' : 'text-gray-500'}`} />
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className={`p-4 rounded-t border ${previewTheme === 'dark' ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <div className={`text-sm ${previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <strong className={previewTheme === 'dark' ? 'text-white' : 'text-gray-900'}>Subject:</strong> {template.subject}
                    </div>
                  </div>
                  <div className={`p-6 border-x border-b rounded-b ${previewTheme === 'dark' ? 'bg-black/50 border-white/10' : 'bg-white border-gray-200'}`}>
                    <div 
                      className="prose max-w-none"
                      style={{
                        color: previewTheme === 'dark' ? '#e5e5e5' : '#1f2937',
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(template.body) }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Edit Mode */
              <div className={`${editorTheme === 'dark' ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`}>
                {/* Template Info */}
                <div className={`p-4 border-b ${editorTheme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${editorTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                        Template Name *
                      </label>
                      <input
                        type="text"
                        value={template.name}
                        onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                        placeholder="e.g., New Proposal"
                        className={`w-full px-3 py-2 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none`}
                      />
                    </div>
                    
                    {isNew && (
                      <div>
                        <label className={`block text-sm font-medium ${editorTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                          Template Key *
                        </label>
                        <input
                          type="text"
                          value={template.template_key}
                          onChange={(e) => setTemplate({ ...template, template_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          placeholder="e.g., new_proposal"
                          className={`w-full px-3 py-2 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none font-mono text-sm`}
                        />
                      </div>
                    )}
                    
                    {isNew && (
                      <div>
                        <label className={`block text-sm font-medium ${editorTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                          Category
                        </label>
                        <select
                          value={template.category}
                          onChange={(e) => setTemplate({ ...template, category: e.target.value })}
                          className={`w-full px-3 py-2 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none`}
                        >
                          {categoryOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4">
                      <label className={`flex items-center gap-2 cursor-pointer ${editorTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <input
                          type="checkbox"
                          checked={template.is_user_editable}
                          onChange={(e) => setTemplate({ ...template, is_user_editable: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">User Editable</span>
                      </label>
                      
                      {!isNew && (
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          template.status === 'published'
                            ? editorTheme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                            : editorTheme === 'dark' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {template.status === 'published' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {template.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Subject */}
                <div className={`p-4 border-b ${editorTheme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <label className={`block text-sm font-medium ${editorTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={template.subject}
                    onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                    placeholder="Email subject line with {{variables}}"
                    className={`w-full px-3 py-2 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none`}
                  />
                </div>
                
                {/* Body Editor with Visual/HTML Toggle */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className={`text-sm font-medium ${editorTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Email Body *
                    </label>
                    
                    {/* Visual/HTML Toggle */}
                    <div className={`flex items-center p-1 rounded-lg ${editorTheme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
                      <button
                        onClick={() => setEditorMode('visual')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          editorMode === 'visual' 
                            ? 'bg-[#A2FF00] text-black' 
                            : editorTheme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Type className="w-4 h-4" />
                        Visual
                      </button>
                      <button
                        onClick={() => setEditorMode('html')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          editorMode === 'html' 
                            ? 'bg-[#A2FF00] text-black' 
                            : editorTheme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Code className="w-4 h-4" />
                        HTML
                      </button>
                    </div>
                  </div>
                  
                  {editorMode === 'visual' ? (
                    /* Visual WYSIWYG Editor */
                    <div className={`rounded-sm border overflow-hidden ${editorTheme === 'dark' ? 'border-white/20 bg-[#0a0a0a]' : 'border-gray-300 bg-white'}`}>
                      <EditorToolbar editor={editor} />
                      <EditorContent editor={editor} />
                    </div>
                  ) : (
                    /* HTML Code Editor */
                    <textarea
                      value={template.body}
                      onChange={(e) => setTemplate({ ...template, body: e.target.value })}
                      placeholder="<p>Dear {{recipient_name}},</p>&#10;&#10;<p>Your email content here...</p>"
                      rows={18}
                      className={`w-full px-4 py-3 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-green-400' : 'bg-gray-900 border-gray-700 text-green-400'} border rounded-sm focus:border-[#A2FF00] outline-none font-mono text-sm leading-relaxed`}
                      style={{ tabSize: 2 }}
                    />
                  )}
                  
                  <p className={`mt-2 text-xs ${editorTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    {editorMode === 'visual' 
                      ? 'Use the toolbar to format text. Click on variables in the sidebar to insert them.'
                      : 'Edit raw HTML directly. Use {{variable_name}} syntax for dynamic content.'}
                  </p>
                </div>
                
                {/* Sender Override */}
                <div className={`p-4 border-t ${editorTheme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Info className={`w-4 h-4 ${editorTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                    <span className={`text-sm ${editorTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Sender Override (Optional)
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm ${editorTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'} mb-1`}>
                        Override Sender Name
                      </label>
                      <input
                        type="text"
                        value={template.override_sender_name || ''}
                        onChange={(e) => setTemplate({ ...template, override_sender_name: e.target.value })}
                        placeholder="Leave empty to use default"
                        className={`w-full px-3 py-2 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none text-sm`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm ${editorTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'} mb-1`}>
                        Override Sender Email
                      </label>
                      <input
                        type="email"
                        value={template.override_sender_email || ''}
                        onChange={(e) => setTemplate({ ...template, override_sender_email: e.target.value })}
                        placeholder="Leave empty to use default"
                        className={`w-full px-3 py-2 ${editorTheme === 'dark' ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none text-sm`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Variables Panel */}
          <div className="lg:col-span-1">
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm sticky top-24`}>
              <button
                onClick={() => setShowVariables(!showVariables)}
                className={`w-full flex items-center justify-between p-4 ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'}`}
              >
                <span className="font-medium">Available Variables</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showVariables ? 'rotate-180' : ''}`} />
              </button>
              
              {showVariables && (
                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  {variables.length > 0 ? (
                    <div className="p-2 space-y-1 max-h-[400px] overflow-y-auto">
                      {variables.map((v, idx) => (
                        <div
                          key={idx}
                          className={`group p-2 rounded ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                        >
                          <div className="flex items-center justify-between">
                            <code className={`text-sm ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                              {`{{${v.name}}}`}
                            </code>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => insertVariable(v.name)}
                                className={`p-1 rounded ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                                title="Insert"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => copyVariable(v.name)}
                                className={`p-1 rounded ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                                title="Copy"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                            {v.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`p-4 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {template.template_key 
                        ? 'No variables defined for this template type'
                        : 'Enter a template key to see available variables'}
                    </div>
                  )}
                </div>
              )}
              
              {/* Quick Tips */}
              <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h4 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Quick Tips</h4>
                <ul className={`text-xs space-y-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <li>• Use <strong>Visual</strong> mode for easy formatting</li>
                  <li>• Switch to <strong>HTML</strong> for full control</li>
                  <li>• Click a variable to insert it</li>
                  <li>• Preview to see final result</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SystemEmailEditor;
